import { useEffect, useRef, useState } from 'react';

import Btn from '../ui/Btn';
import { Card, Spinner, Badge } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { supabase } from '../../lib/supabase';
import { api } from '../../lib/api';
import { getTeamOwnerPlan, getTeamStorageCount } from '../../lib/plan';
import { useIsMobile } from '../../lib/useBreakpoint';

/**
 * QuickUploadModal — opened from the Hub or MyDocs.
 *
 * Flow:
 *   1. Pick a workspace from the list of teams the user has access to
 *   2. (optional) pick a template — applied to ALL files in the batch
 *   3. Drop/pick one OR MANY files → batch upload + classify
 *   4. AI classifier runs per-file in the background (throttled)
 *
 * Batch processing:
 *   - Files are uploaded sequentially (one at a time) to keep memory + storage
 *     pressure low. Classify pings go through the throttled limiter in api.js.
 *   - Each file tracks its own status: queued | uploading | done | failed
 *   - Individual failures don't abort the batch; failed files get a Retry button.
 *
 * Background-progress mode (MyDocs):
 *   - `instantClose`: on file selection the modal closes immediately and the
 *     batch keeps processing; progress renders in the parent's "In progress"
 *     section instead of a blocking modal. The parent must keep this component
 *     MOUNTED (use `visible` instead of conditional rendering) so the upload
 *     loop survives the close.
 *   - `onBatchChange(batch)`: reports every batch state change to the parent.
 *   - `onBatchApi({ retry, dismiss })`: hands the parent per-item controls.
 */
export default function QuickUploadModal({ teams, lockedTeamId = null, visible = true, instantClose = false, onBatchChange, onBatchApi, onClose, onUploaded, onClassifyError }) {
  const isMobile = useIsMobile();
  // If lockedTeamId is provided, skip the team picker step entirely.
  const [step, setStep]               = useState(lockedTeamId ? 'pick-template' : 'pick-team');
  const [selectedTeamId, setSelectedTeamId] = useState(lockedTeamId);
  const [membership, setMembership]   = useState(null);
  const [templates, setTemplates]     = useState([]);
  const [tplLoading, setTplLoading]   = useState(false);
  const [selectedTplId, setSelectedTplId] = useState(null); // null = unclassified
  const [error, setError]             = useState(null);

  // Batch state: array of { id, file, status, error?, uploadId? }
  // status: 'queued' | 'uploading' | 'done' | 'failed'
  const [batch, setBatch] = useState([]);

  // Background-progress mode: mirror batch state up to the parent, and expose
  // per-item retry/dismiss. retry goes through a ref so the parent's stable
  // callback always reaches the CURRENT retryItem closure (not a stale batch).
  const retryRef = useRef(null);
  useEffect(() => { onBatchChange?.(batch); }, [batch, onBatchChange]);
  useEffect(() => {
    onBatchApi?.({
      retry: (id) => retryRef.current?.(id),
      dismiss: (id) => setBatch(prev => prev.filter(b => b.id !== id)),
    });
  }, [onBatchApi]);

  // Once a team is picked, load templates assigned to my role for that team
  useEffect(() => {
    if (!selectedTeamId) return;
    let cancelled = false;
    (async () => {
      setTplLoading(true);
      setError(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (!userId) throw new Error('Not signed in.');

        // My membership in this team
        const { data: mem } = await supabase
          .from('team_members')
          .select('id, role_id, team_roles ( id, is_staff )')
          .eq('team_id', selectedTeamId)
          .eq('user_id', userId)
          .maybeSingle();
        if (!mem) throw new Error('You are not a member of this workspace.');
        if (cancelled) return;
        setMembership(mem);

        // If I'm staff, fetch ALL templates for the team (so I can upload as anything)
        // If I'm a client, only fetch templates assigned to my role
        let tpls = [];
        if (mem.team_roles?.is_staff) {
          const { data } = await supabase
            .from('document_templates')
            .select('id, label, key, category, required')
            .eq('team_id', selectedTeamId)
            .order('label');
          tpls = data || [];
        } else {
          const { data } = await supabase
            .from('template_role_assignments')
            .select('document_templates ( id, label, key, category, required )')
            .eq('team_id', selectedTeamId)
            .eq('role_id', mem.role_id);
          tpls = (data || [])
            .map(a => a.document_templates)
            .filter(Boolean);
        }
        if (cancelled) return;
        setTemplates(tpls);
        setStep('pick-template');
      } catch (err) {
        console.error('Quick upload load failed:', err);
        if (!cancelled) setError(err.message || 'Could not load that workspace.');
      } finally {
        if (!cancelled) setTplLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTeamId]);

  // Process a single batch item: upload to storage, insert row, fire classify.
  // Updates the batch state in place (status / uploadId / error).
  const processOne = async (item) => {
    const tpl = templates.find(t => t.id === selectedTplId);

    // Update item status to uploading
    setBatch(prev => prev.map(b => b.id === item.id ? { ...b, status: 'uploading' } : b));

    try {
      const ext = (item.file.name.split('.').pop() || 'bin').toLowerCase();
      const safeKey = (tpl?.key || 'unclassified').replace(/[^a-z0-9_-]/gi, '_');
      // Add a per-file random suffix so two files uploaded in the same tick
      // can't collide on Date.now().
      const path = `${selectedTeamId}/${membership.id}/${safeKey}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}.${ext}`;

      const { error: upErr } = await supabase.storage
        .from('client-docs')
        .upload(path, item.file, { upsert: false, contentType: item.file.type || undefined });
      if (upErr) throw new Error('Upload failed: ' + upErr.message);

      const { data: inserted, error: insErr } = await supabase
        .from('document_uploads')
        .insert({
          team_id: selectedTeamId,
          member_id: membership.id,
          template_id: tpl?.id || null,
          storage_path: path,
          original_filename: item.file.name,
          display_name: tpl?.label || null,
          status: 'in_review',
        })
        .select()
        .single();
      if (insErr) throw insErr;

      // Classifier ping — throttled (max 2 concurrent, 250ms stagger across batch).
      // Fire & forget; we don't block the batch on AI completion.
      api.classifyUploadThrottled({
        team_id: selectedTeamId,
        upload_id: inserted.id,
        storage_path: path,
        suggested_template_id: tpl?.id || null,
      })
        .then(r => console.log('[classify] ✓ ok', inserted.id, r))
        .catch(err => {
          console.warn('[classify] ✗ failed for', inserted.id, err.message || err);
          const code = err?.data?.error;
          if ((code === 'limit_reached' || code === 'storage_limit') && onClassifyError) {
            onClassifyError(err.data);
          }
        });

      // Mark item done + notify parent immediately so the new row shows up
      // in the parent's list as it completes.
      setBatch(prev => prev.map(b => b.id === item.id
        ? { ...b, status: 'done', uploadId: inserted.id }
        : b
      ));
      onUploaded?.(inserted, selectedTeamId);
    } catch (err) {
      console.error('Batch item failed:', item.file.name, err);
      setBatch(prev => prev.map(b => b.id === item.id
        ? { ...b, status: 'failed', error: err.message || 'Upload failed' }
        : b
      ));
    }
  };

  // Start batch processing — runs items sequentially in original order.
  // We process one at a time to avoid: (a) Supabase Storage rate limits,
  // (b) overwhelming the backend classifier (further throttled in api.js).
  const runBatch = async (items) => {
    for (const item of items) {
      await processOne(item);
    }
  };

  // Entry point — called by the dropzone + multi-select picker with one+ files
  const onFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (!selectedTeamId || !membership) return;

    const files = Array.from(fileList);

    // Per-file size validation — keep oversized files OUT of the batch
    // and surface a single banner with their names.
    const oversize = files.filter(f => f.size > 25 * 1024 * 1024);
    const accepted = files.filter(f => f.size <= 25 * 1024 * 1024);
    if (oversize.length > 0) {
      setError(`Skipped ${oversize.length} file${oversize.length === 1 ? '' : 's'} over 25 MB: ${oversize.map(f => f.name).join(', ')}`);
    } else {
      setError(null);
    }
    if (accepted.length === 0) return;

    // Pre-flight storage limit check — done once, not per file. If the team
    // is already over the storage cap, block the whole batch.
    try {
      const { plan, limits } = await getTeamOwnerPlan(selectedTeamId);
      if (plan === 'free') {
        const currentCount = await getTeamStorageCount(selectedTeamId);
        if (currentCount + accepted.length > limits.storage) {
          if (onClassifyError) {
            onClassifyError({
              error: 'storage_limit',
              type: 'storage',
              plan,
              used: currentCount,
              limit: limits.storage,
            });
          }
          onClose?.();
          return;
        }
      }
    } catch (err) {
      console.warn('[QuickUpload] Storage check failed:', err);
    }

    // Build batch, then either hand progress to the parent (instantClose:
    // close now, keep processing in the background — append so a second
    // batch started mid-flight doesn't orphan the first) or switch to the
    // classic in-modal processing UI.
    const items = accepted.map(f => ({
      id: `${f.name}_${f.size}_${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      status: 'queued',
    }));
    if (instantClose) {
      setBatch(prev => [...prev, ...items]);
      onClose?.();
    } else {
      setBatch(items);
      setStep('batch-processing');
    }
    runBatch(items);
  };

  // Retry a single failed item (e.g. transient network error on one file)
  const retryItem = (itemId) => {
    const item = batch.find(b => b.id === itemId);
    if (!item || item.status === 'uploading') return;
    setBatch(prev => prev.map(b => b.id === itemId ? { ...b, status: 'queued', error: undefined } : b));
    processOne(item);
  };

  // Keep the current retryItem closure reachable from the stable onBatchApi hook
  retryRef.current = retryItem;

  const selectedTeam = teams.find(t => t.id === selectedTeamId);

  // Background-progress mode: stay mounted (upload loop + state live on) but
  // render nothing while hidden.
  if (!visible) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10, 15, 46, 0.55)',
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: isMobile ? 'none' : '1px solid var(--border)',
          borderRadius: isMobile ? 0 : 'var(--r-xl)',
          boxShadow: 'var(--shadow-xl)',
          width: '100%',
          maxWidth: isMobile ? 'none' : 520,
          maxHeight: isMobile ? '100dvh' : '90vh',
          height: isMobile ? '100dvh' : 'auto',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: isMobile ? '14px 18px' : '20px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexShrink: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
              Quick upload
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
              {step === 'pick-team' && 'Choose a workspace'}
              {step === 'pick-template' && (selectedTeam?.name || 'Pick a template')}
              {step === 'batch-processing' && (() => {
                const done = batch.filter(b => b.status === 'done').length;
                const failed = batch.filter(b => b.status === 'failed').length;
                const allFinished = batch.every(b => b.status === 'done' || b.status === 'failed');
                if (!allFinished) return `Uploading ${done + 1} of ${batch.length}…`;
                if (failed === 0) return `All ${batch.length} uploaded`;
                return `${done} uploaded · ${failed} failed`;
              })()}
            </div>
          </div>
          <Btn variant="ghost" size="sm" icon={Icon.x} onClick={onClose} title="Close" />
        </div>

        <div style={{ padding: isMobile ? '16px 18px' : '20px 24px', overflowY: 'auto', flex: 1 }}>
          {error && (
            <div style={{
              padding: '10px 12px', marginBottom: 14,
              background: 'var(--danger-bg)',
              border: '1px solid rgba(239,68,68,0.22)',
              color: 'var(--danger)', borderRadius: 8,
              fontSize: 12.5,
            }}>
              {error}
            </div>
          )}

          {/* STEP 1: Pick team */}
          {step === 'pick-team' && (
            <>
              {teams.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
                  You're not a member of any workspaces yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {teams.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setSelectedTeamId(t.id)}
                      style={{
                        padding: '12px 14px', textAlign: 'left',
                        background: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--r-md)',
                        cursor: 'pointer', fontFamily: 'inherit',
                        display: 'flex', alignItems: 'center', gap: 12,
                        transition: 'all 0.12s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--accent)';
                        e.currentTarget.style.background = 'var(--accent-bg)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border)';
                        e.currentTarget.style.background = 'var(--surface)';
                      }}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                        background: t.primary_color || '#0a0f2e',
                        color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700,
                      }}>
                        {(t.name || '?').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13.5, fontWeight: 600, color: 'var(--text)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {t.name}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>
                          {t.roleName || (t.isOwner ? 'Owner' : 'Member')}
                        </div>
                      </div>
                      <Icon.chevR size={14} stroke="var(--text-muted)" />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* STEP 2: Pick template + upload */}
          {step === 'pick-template' && (
            <>
              {tplLoading ? (
                <div style={{ padding: 24, textAlign: 'center' }}>
                  <Spinner size={16} color="var(--accent)" />
                </div>
              ) : (
                <>
                  <label style={{
                    fontSize: 12, fontWeight: 600, color: 'var(--text-2)',
                    display: 'block', marginBottom: 6,
                  }}>
                    Document type <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· optional</span>
                  </label>
                  <select
                    value={selectedTplId || ''}
                    onChange={(e) => setSelectedTplId(e.target.value || null)}
                    className="mi-focusable"
                    style={{
                      width: '100%', height: 40, padding: '0 12px',
                      fontSize: 13.5, fontFamily: 'inherit', color: 'var(--text)',
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 'var(--r-md)', cursor: 'pointer', marginBottom: 6,
                    }}
                  >
                    <option value="">Let AI figure it out</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 18 }}>
                    {templates.length === 0
                      ? 'No templates defined for this workspace yet — file will be marked unclassified.'
                      : 'AI will classify your document automatically. Only pick a type if you already know what this is.'}
                  </div>

                  <FileDropZone onFiles={onFiles} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14 }}>
                    {!lockedTeamId && (
                      <Btn
                        variant="ghost"
                        size="sm"
                        icon={Icon.arrowL}
                        onClick={() => {
                          setStep('pick-team');
                          setSelectedTeamId(null);
                          setSelectedTplId(null);
                          setMembership(null);
                          setTemplates([]);
                        }}
                      >
                        Back to workspaces
                      </Btn>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* STEP 3: Batch processing — shows per-file progress */}
          {step === 'batch-processing' && (
            <BatchList
              items={batch}
              teamName={selectedTeam?.name}
              onRetry={retryItem}
              onUploadMore={() => {
                // Reset to template picker so user can add another batch
                setBatch([]);
                if (lockedTeamId) {
                  setStep('pick-template');
                } else {
                  setStep('pick-team');
                  setSelectedTeamId(null);
                  setSelectedTplId(null);
                  setMembership(null);
                  setTemplates([]);
                }
              }}
              onDone={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function FileDropZone({ onFiles }) {
  const [drag, setDrag] = useState(false);

  return (
    <label
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
      }}
      style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '32px 20px',
        border: `1.5px dashed ${drag ? 'var(--accent)' : 'var(--border-strong)'}`,
        background: drag ? 'var(--accent-bg)' : 'var(--surface-2)',
        borderRadius: 'var(--r-lg)',
        cursor: 'pointer',
        transition: 'all 0.12s',
      }}
    >
      <input
        type="file"
        multiple
        accept=".pdf,.png,.jpg,.jpeg,.heic,.webp,.doc,.docx"
        onChange={(e) => {
          if (e.target.files?.length) onFiles(e.target.files);
          // Allow re-selecting the same file(s) again later
          e.target.value = '';
        }}
        style={{ display: 'none' }}
      />
      <Icon.upload size={26} stroke={drag ? 'var(--accent-600)' : 'var(--text-3)'} />
      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 10, color: 'var(--text)' }}>
        {drag ? 'Drop to upload' : 'Drag files here, or click to browse'}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
        PDF, PNG, JPG, HEIC, WEBP, DOC, DOCX · up to 25 MB · multiple supported
      </div>
    </label>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// BatchList — per-file progress + retry for failed items
// ────────────────────────────────────────────────────────────────────────────
function BatchList({ items, teamName, onRetry, onUploadMore, onDone }) {
  const done = items.filter(i => i.status === 'done').length;
  const failed = items.filter(i => i.status === 'failed').length;
  const inProgress = items.filter(i => i.status === 'uploading' || i.status === 'queued').length;
  const allFinished = inProgress === 0;

  return (
    <div>
      {/* Summary banner */}
      <div style={{
        padding: '10px 12px', marginBottom: 12,
        background: failed > 0 ? 'var(--warn-bg)' : (allFinished ? 'var(--ok-bg)' : 'var(--info-bg)'),
        border: `1px solid ${failed > 0 ? 'rgba(245,158,11,0.22)' : (allFinished ? 'rgba(61,199,91,0.22)' : 'rgba(59,130,246,0.22)')}`,
        borderRadius: 8,
        fontSize: 12.5,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        {allFinished
          ? <Icon.check size={14} stroke={failed > 0 ? 'var(--warn)' : 'var(--ok)'} />
          : <Spinner size={12} color="var(--info)" />}
        <span style={{ color: 'var(--text-2)' }}>
          {!allFinished && (
            <>Uploading <strong>{done + 1}</strong> of <strong>{items.length}</strong>…</>
          )}
          {allFinished && failed === 0 && (
            <>All <strong>{items.length}</strong> uploaded — AI classifier is running. Reviewers in {teamName || 'this workspace'} will be notified.</>
          )}
          {allFinished && failed > 0 && (
            <><strong>{done}</strong> uploaded, <strong>{failed}</strong> failed. Tap retry on any failed file below.</>
          )}
        </span>
      </div>

      {/* Per-file list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
        {items.map(item => (
          <BatchRow key={item.id} item={item} onRetry={onRetry} />
        ))}
      </div>

      {/* Footer actions — only when batch is fully done */}
      {allFinished && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="outline" size="sm" onClick={onUploadMore}>
            Upload more
          </Btn>
          <Btn variant="primary" size="sm" onClick={onDone}>
            Done
          </Btn>
        </div>
      )}
    </div>
  );
}

function BatchRow({ item, onRetry }) {
  const statusUi = {
    queued:    { icon: <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--text-muted)' }} />, label: 'Queued', color: 'var(--text-3)' },
    uploading: { icon: <Spinner size={11} color="var(--info)" />, label: 'Uploading…', color: 'var(--info)' },
    done:      { icon: <Icon.check size={12} stroke="var(--ok)" strokeWidth={3} />, label: 'Uploaded', color: 'var(--ok)' },
    failed:    { icon: <Icon.alert size={12} stroke="var(--danger)" />, label: 'Failed', color: 'var(--danger)' },
  }[item.status] || { icon: null, label: item.status, color: 'var(--text-3)' };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 10px',
      background: 'var(--surface-2)', border: '1px solid var(--border)',
      borderRadius: 8, fontSize: 12.5,
    }}>
      <div style={{
        width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {statusUi.icon}
      </div>
      <div style={{
        flex: 1, minWidth: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        color: 'var(--text)', fontWeight: 500,
      }}>
        {item.file.name}
      </div>
      <span style={{
        fontSize: 11, fontWeight: 600, color: statusUi.color,
        whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        {statusUi.label}
      </span>
      {item.status === 'failed' && (
        <Btn variant="ghost" size="xs" icon={Icon.refresh} onClick={() => onRetry(item.id)}>
          Retry
        </Btn>
      )}
    </div>
  );
}
